// Is the operator's microphone actually producing sound?
//
// WHY THIS EXISTS. A call can be perfectly healthy on every signal we had and still be useless: the
// peer connects, the stream publishes as opus at 48 kHz, the helmet attaches as a reader — and the
// wearer hears nothing, because the track the browser handed us is silent. macOS hands Chrome
// whatever its default input is, which can be a device with no live input at all, and nothing in
// WebRTC treats that as an error. Measured on a real call: the mic leg connected with a 27 ms round
// trip and `totalAudioEnergy` of exactly 0 for its whole duration.
//
// So the operator found out by asking the wearer whether they could be heard. This measures the
// track itself and says so on screen instead.
//
// It reads the LOCAL track rather than `getStats`, deliberately: an energy figure from the encoder
// tells you what was sent, while this tells you what the microphone is picking up, which is the
// thing that is actually broken in this failure. It is also the same number a volume meter would
// show, so it can drive one later.

import { useEffect, useRef, useState } from 'react'

/** Below this peak amplitude (0-1) a frame counts as silence. Room tone sits well above it. */
export const SILENCE_THRESHOLD = 0.01

/** How long unbroken silence must last before we say anything. Long enough not to flag a pause. */
export const SILENCE_GRACE_MS = 8_000

const POLL_MS = 500

export interface MicLevel {
  /** Peak amplitude of the last sampled frame, 0-1. Null before the first sample. */
  peak: number | null
  /** True once the microphone has been silent for `SILENCE_GRACE_MS` without a break. */
  silent: boolean
}

/**
 * Watch a live microphone stream.
 *
 * Passing `null` (no call, or the mic released) resets everything — a stale "silent" badge outliving
 * the call that produced it would be worse than no badge.
 */
export function useMicLevel(stream: MediaStream | null, enabled = true): MicLevel {
  const [peak, setPeak] = useState<number | null>(null)
  const [silent, setSilent] = useState(false)
  const silentSinceRef = useRef<number | null>(null)

  useEffect(() => {
    setPeak(null)
    setSilent(false)
    silentSinceRef.current = null

    if (!stream || !enabled) return

    const track = stream.getAudioTracks()[0]

    if (!track) return

    // `webkitAudioContext` for older Safari; absent entirely in jsdom, where this simply does
    // nothing rather than throwing into a call panel.
    const Ctor: typeof AudioContext | undefined =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext

    if (!Ctor) return

    let ctx: AudioContext

    try {
      ctx = new Ctor()
    } catch {
      return
    }

    const source = ctx.createMediaStreamSource(stream)
    const analyser = ctx.createAnalyser()

    analyser.fftSize = 2048
    source.connect(analyser)

    const buf = new Float32Array(analyser.fftSize)
    const id = setInterval(() => {
      analyser.getFloatTimeDomainData(buf)

      let max = 0

      for (const v of buf) max = Math.max(max, Math.abs(v))

      setPeak(max)

      const now = performance.now()

      // A MUTED track is not a broken one — the operator chose that, and the panel already says so.
      if (max >= SILENCE_THRESHOLD || !track.enabled || track.muted) {
        silentSinceRef.current = null
        setSilent(false)

        return
      }

      silentSinceRef.current ??= now
      setSilent(now - silentSinceRef.current >= SILENCE_GRACE_MS)
    }, POLL_MS)

    return () => {
      clearInterval(id)
      source.disconnect()
      void ctx.close().catch(() => undefined)
    }
  }, [stream, enabled])

  return { peak, silent }
}
